/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 */

//Services
import PortalApi from "@/services/Portal";
import SchedulerApi from "@/services/Scheduler";
import ScheduleBroadcasterAPI from "@/services/ScheduleBroadcaster";

//Enums
import ShowIntegrationType from "@/enums/ShowIntegrationType";

export default {
	state: {
		currentShow: {},

		schedulerEvents: {},

		schedulerSubscriptions: {},

		schedulerUsageCounterMap: {}
	},

	getters: {
		currentShow: (state) => (stationID) => {
			return state.currentShow[stationID];
		},

		schedulerEventsByPeriod: (state) => ({stationID, period}) => {
			const {from, to} = period;
			return state.schedulerEvents[stationID]?.[`${from}-${to}`];
		},

		schedulerEventById: (state) => ({stationID, period, eventID}) => {
			return state.schedulerEvents[stationID]?.[`${period.from}-${period.to}`]?.find(
				({id, override_id}) => id === eventID || override_id === eventID
			);
		},

		schedulerSubscriptions: (state) => (stationID) => {
			return state.schedulerSubscriptions[stationID];
		}
	},

	mutations: {
		incrementSchedulerUsageCounterMap(state, stationID) {
			if (!state.schedulerUsageCounterMap[stationID]) {
				state.schedulerUsageCounterMap[stationID] = 0;
			}
			state.schedulerUsageCounterMap[stationID]++;
		},

		setCurrentShow(state, {stationID, show}) {
			state.currentShow[stationID] = show;
		},

		resetCurrentShow(state, stationID) {
			delete state.currentShow[stationID];
		},

		setSchedulerEvents(state, {stationID, period, events}) {
			if (!state.schedulerEvents[stationID]) {
				state.schedulerEvents[stationID] = {};
			}
			state.schedulerEvents[stationID][`${period.from}-${period.to}`] = events;
		},

		setSchedulerSubscriptions(state, {stationID, subscriptions}) {
			state.schedulerSubscriptions[stationID] = subscriptions;
		},

		clearSchedulerEventsAndSubscriptions(state, stationID) {
			if (state.schedulerUsageCounterMap[stationID] === 1) {
				delete state.schedulerEvents[stationID];
				delete state.schedulerSubscriptions[stationID];
			}
			state.schedulerUsageCounterMap[stationID]--;
		},

		resetSchedulerState(state) {
			state.currentShow = {};
			state.schedulerEvents = {};
			state.schedulerSubscriptions = {};
		}
	},

	actions: {
		getShow({getters, commit}, {stationID, showID, current = false}) {
			const currentShow = getters.currentShow(stationID);
			if (currentShow?.id === showID) {
				return currentShow;
			}
			return PortalApi.getShow(showID).then(response => {
				if (current) {
					//Ignore Split Shows
					if (response.data.integration_type !== ShowIntegrationType.Split) {
						commit("setCurrentShow", {stationID, show: response.data});
					}
				}
				return response.data;
			});
		},

		getShowSettings(state, showID) {
			return PortalApi.getShowSettings(showID).then(response => response.data);
		},

		getStationSchedulerEvents({getters, commit}, {stationID, period, useCache = true} = {}) {
			let schedulerEvents = useCache ? getters.schedulerEventsByPeriod({stationID, period}) : null;

			if (!schedulerEvents) {
				return SchedulerApi
					.getSchedulerProjectedEvents({
						stationID,
						params: period
					})
					.then((response) => {
						commit("setSchedulerEvents", {stationID, period, events: response.data});
						return response.data;
					})
					.catch(error => {
						console.log(error);
						commit(
							"SHOW_NOTIFICATION_WITH_TYPE",
							{message: "An error occurred when trying to load the scheduler events."}
						);
						return [];
					});
			}
			else {
				return new Promise((resolve) => {
					return resolve(schedulerEvents);
				});
			}
		},

		getSchedulerSubscriptions({commit}, stationID) {
			return SchedulerApi.getSchedulerSubscriptions(stationID).then(response => {
				commit("setSchedulerSubscriptions", {stationID, subscriptions: response.data});
			});
		},

		resetStationEvent(state, params) {
			return ScheduleBroadcasterAPI.resetStationEvent(params);
		},

		syncStationEvent(state, params) {
			return ScheduleBroadcasterAPI.syncStationEvent(params);
		},

		createEventOverride(state, payload) {
			return SchedulerApi.createEventOverride(payload);
		},

		updateEventOverride(state, payload) {
			return SchedulerApi.updateEventOverride(payload);
		},

		cancelEvent(state, params) {
			return SchedulerApi.cancelEvent(params);
		},

		restoreEvent(state, params) {
			return SchedulerApi.restoreEvent(params);
		},

		updateScheduleEvent(state, payload) {
			return SchedulerApi.updateScheduleEvent(payload);
		},

		syncShowEvent(state, params) {
			return ScheduleBroadcasterAPI.syncShowEvent(params);
		}
	}
};
